import React, { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";

/**
 * Captures the browser's `beforeinstallprompt` event and exposes a custom
 * "Install" button. Hidden when:
 *   - The app is already running in standalone (i.e. installed) mode
 *   - The browser doesn't support PWA install (Safari iOS, etc.)
 *   - The user dismissed the prompt this session
 *
 * Visible as a small pill in the topbar so it doesn't fight for attention.
 * Mobile users see a slightly larger floating prompt at the bottom on
 * first visit only — controlled via localStorage so we don't pester them.
 */
export default function PwaInstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Already running as installed PWA → never show.
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) setInstalled(true);
  }, []);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Honor a session-level dismissal so we don't pop up every render
      if (window.sessionStorage.getItem("pwa-dismissed") === "1") {
        setHidden(true);
      }
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || !deferredPrompt || hidden) return null;

  const handleInstall = async () => {
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch { /* ignore */ }
    setDeferredPrompt(null);
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    window.sessionStorage.setItem("pwa-dismissed", "1");
    setHidden(true);
  };

  return (
    <button
      type="button"
      onClick={handleInstall}
      data-testid="pwa-install-button"
      title="Install Breakout Scanner as an app"
      className="group inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-mod/40 rounded-sm font-mono text-[10px] uppercase tracking-widest text-mod hover:bg-mod/10 hover:border-mod transition"
    >
      <DownloadSimple size={12} weight="bold" />
      <span>Install App</span>
      <span
        role="button"
        aria-label="Dismiss install prompt"
        onClick={handleDismiss}
        data-testid="pwa-install-dismiss"
        className="ml-0.5 p-0.5 rounded-sm opacity-60 hover:opacity-100 hover:bg-mod/20"
      >
        <X size={9} weight="bold" />
      </span>
    </button>
  );
}
